import type { CanvasNode } from '../services/canvasService'
import type { TextStyle } from '../data/miroFonts'
import { encodeRef } from '../services/imageRefs'
import type { SentMoodboard, SentPicture } from './send'

/**
 * A mood board as a frame on a project's board, and back (Huanrui, 2026-10-12)
 * -------------------------------------------------------------
 * Copy to project puts the mood board on a board as a frame -- not one flat
 * picture -- at the sheet's own 1920 x 1080: its ground is the frame's
 * colour, every picture (already cut) an image where it sat, the words Text,
 * the colour strip small colour pictures. The frame is marked `moodboard`.
 *
 * Selected and sent to Miro, such a frame is read back into a mood board from
 * what is in it NOW -- moved, recoloured, retitled, words changed -- and goes
 * the mood board's way (the panel draws one 16:9 frame with everything in
 * it), so Miro gets the same frame. Anything added to it later goes too.
 */

export const MOODBOARD_W = 1920
export const MOODBOARD_H = 1080
const FRAME_ID = 'moodboard-frame'

export interface FramePiece {
  title: string
  ground: string
  /**
   * Each picture by its file in the project (`fileId`), or, shown whole,
   * by the library picture itself (`assetId`, 2026-10-16: the original, not a copy).
   */
  pictures: { fileId?: string; assetId?: string; title: string; x: number; y: number; w: number; h: number; float?: boolean }[]
  texts: { text: string; x: number; y: number; w: number; h: number; style: TextStyle }[]
  swatches: { fileId: string; color: string; x: number; y: number; w: number; h: number }[]
}

/** The nodes, the frame first (it lies beneath), positions from the frame's corner. */
export function moodboardFramePiece(piece: FramePiece): CanvasNode[] {
  const page = 'page-1'
  return [
    {
      id: FRAME_ID,
      type: 'frame',
      page,
      position: { x: 0, y: 0 },
      width: MOODBOARD_W,
      height: MOODBOARD_H,
      data: { name: piece.title || 'Mood board', color: piece.ground, moodboard: true },
    },
    ...piece.pictures.map((one, n) => ({
      id: `moodboard-picture-${n}`,
      type: 'image' as const,
      page,
      position: { x: one.x, y: one.y },
      width: one.w,
      height: one.h,
      data: {
        ...(one.assetId ? { assetId: one.assetId } : { fileId: one.fileId }),
        title: one.title,
        frame: FRAME_ID,
        aspect: one.w / one.h,
        // A lifted picture, drawn with its outline and turn (float.ts): over the rest.
        ...(one.float ? { float: true } : {}),
      },
    })),
    ...piece.swatches.map((one, n) => ({
      id: `moodboard-colour-${n}`,
      type: 'image' as const,
      page,
      position: { x: one.x, y: one.y },
      width: one.w,
      height: one.h,
      data: { fileId: one.fileId, title: one.color, frame: FRAME_ID, aspect: one.w / one.h, swatch: one.color },
    })),
    ...piece.texts.map((one, n) => ({
      id: `moodboard-text-${n}`,
      type: 'label' as const,
      page,
      position: { x: one.x, y: one.y },
      width: one.w,
      data: { text: one.text, textStyle: one.style, frame: FRAME_ID },
    })),
  ]
}

export const isMoodboardFrame = (data: Record<string, unknown> | undefined): boolean => Boolean(data?.moodboard)

/** What the board knows of a node, enough to read a frame back. */
export interface FrameMember {
  type: string
  x: number
  y: number
  w: number
  h: number
  data: Record<string, unknown>
  /** The picture's own width / height, when known: a box of another shape shows it cut, as the board does. */
  ratio?: number
}

/**
 * Any frame on a project's board, as it is now, into what the panel draws
 * (Huanrui, 2026-10-12: a frame goes to Miro with its own layout, not as a
 * row of pictures of one height). The frame keeps its shape, its longer side
 * 1920 sheet units; everything in it keeps its place and size relative to it.
 */
export function moodboardFromFrame(
  frame: { x: number; y: number; w: number; h: number; data: Record<string, unknown> },
  members: FrameMember[],
  fallbackColor = '#ffffff',
): SentMoodboard {
  const k = MOODBOARD_W / Math.max(1, frame.w, frame.h)
  const at = (one: FrameMember) => ({
    x: (one.x - frame.x) * k,
    y: (one.y - frame.y) * k,
    w: one.w * k,
    h: one.h * k,
  })
  const pictures: SentPicture[] = []
  // Lifted pictures (2026-10-23): already drawn, placed after the rest so they stay on top.
  const floats: SentPicture[] = []
  const swatches: SentMoodboard['swatches'] = []
  const texts: SentMoodboard['texts'] = []
  for (const one of members) {
    const data = one.data
    if (one.type === 'label') {
      const text = typeof data.text === 'string' ? data.text.trim() : ''
      const style = data.textStyle as TextStyle | undefined
      if (!text || !style) continue
      texts.push({ text, ...at(one), style: { ...style, size: style.size * k } })
      continue
    }
    if (typeof data.swatch === 'string' && /^#[0-9a-f]{6}$/i.test(data.swatch)) {
      swatches.push({ ...at(one), color: data.swatch })
      continue
    }
    const ref =
      typeof data.fileId === 'string'
        ? encodeRef({ store: 'project', id: data.fileId })
        : typeof data.assetId === 'string'
          ? encodeRef({ store: 'library', id: data.assetId })
          : ''
    if (!ref) continue
    const box = one.w / Math.max(1, one.h)
    const crop = one.ratio && Math.abs(one.ratio / box - 1) > 0.01 ? centreCrop(one.ratio, box) : { x: 0, y: 0, w: 1, h: 1 }
    const picture = { ref, title: typeof data.title === 'string' ? data.title : 'Picture', ...at(one), crop }
    if (data.float) floats.push(picture)
    else pictures.push(picture)
  }
  const name = typeof frame.data.name === 'string' ? frame.data.name.trim() : ''
  const own = typeof frame.data.color === 'string' && /^#[0-9a-f]{6}$/i.test(frame.data.color) ? frame.data.color : ''
  return {
    title: name || 'Frame',
    background: own || fallbackColor,
    width: frame.w * k,
    height: frame.h * k,
    pictures,
    ...(floats.length > 0 ? { floats } : {}),
    texts,
    swatches,
  }
}

/** The middle of a picture, cut to a box of another shape (CSS object-fit: cover). */
function centreCrop(picture: number, box: number) {
  if (picture > box) {
    const w = box / picture
    return { x: (1 - w) / 2, y: 0, w, h: 1 }
  }
  const h = picture / box
  return { x: 0, y: (1 - h) / 2, w: 1, h }
}
